import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Map, ChevronDown, ArrowRight, ClipboardList, BookOpen } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./ui/dialog";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { RoadmapSetup } from "./roadmap/RoadmapSetup";
import { Milestone, PhaseInfo } from "./roadmap/Milestone";
import { PhaseDetail } from "./roadmap/PhaseDetail";
import { SummaryStrip } from "./roadmap/SummaryStrip";

const DETAIL_PHASES = new Set(["application", "testing", "station", "board_prep", "board", "chief"]);

export function RoadMap() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const dismissKey = `fireprep-eval-dismissed:${user?.user_id || "anon"}`;
  const [data, setData] = useState(null);
  const [open, setOpen] = useState({});
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [detail, setDetail] = useState(null);
  // null = unknown until the server-side onboarding state loads; the modal auto-opens once for a genuinely new account
  // (no Road Map profile, no server "seen/dismissed" record) and never re-opens after completion or dismissal.
  const [dismissed, setDismissed] = useState(null);

  const openFirst = (d) => {
    const first = d.phases.find((p) => !p.flex && p.items.some((i) => i.counts && !d.done.includes(i.id)));
    setOpen(first ? { [first.id]: true } : {});
  };
  useEffect(() => {
    api.get("/roadmap").then(({ data }) => { setData(data); openFirst(data); }).catch(() => {});
    api.get("/onboarding").then(({ data }) => {
      // Server state decides. A legacy browser-only dismissal (older builds stored it in localStorage) is migrated to the
      // server once for real accounts; admin preview sandboxes ignore it so a "fresh" sandbox behaves like a brand-new user.
      const legacyDismissed = localStorage.getItem(dismissKey) === "1" && !String(user?.user_id || "").endsWith("__preview");
      if (data.needed && legacyDismissed) api.post("/onboarding/skip").catch(() => {});
      setDismissed(!data.needed || legacyDismissed);
    }).catch(() => setDismissed(true));
  }, [dismissKey, user?.user_id]);
  if (!data) return null;

  const isDone = (id) => data.done.includes(id);
  const toggle = async (id) => {
    const done = isDone(id) ? data.done.filter((d) => d !== id) : [...data.done, id];
    setData((d) => ({ ...d, done }));
    try { const { data: r } = await api.put("/roadmap", { done }); setData(r); }
    catch { toast.error("Could not save your road map."); }
  };
  const saveProfile = async (profile) => {
    setSaving(true);
    try { const { data: r } = await api.put("/roadmap/profile", profile); setData(r); openFirst(r); setEditing(false); toast.success("Road map personalized."); }
    catch { toast.error("Could not save your answers."); }
    finally { setSaving(false); }
  };
  const goTo = (id) => {
    const ph = data.phases.find((p) => p.items.some((i) => i.id === id));
    if (ph) { setOpen((o) => ({ ...o, [ph.id]: true })); setTimeout(() => document.querySelector(`[data-testid="roadmap-row-${id}"]`)?.scrollIntoView({ behavior: "smooth", block: "center" }), 60); }
  };

  const showSetup = editing || (!data.profile && dismissed === false); // only once both /roadmap and /onboarding have loaded
  const closeSetup = () => {
    if (data.profile) { setEditing(false); return; }
    setDismissed(true); localStorage.setItem(dismissKey, "1");
    api.post("/onboarding/skip").catch(() => {}); // server-side record: the evaluation was shown and dismissed
  };

  return (
    <section className="mt-10" data-testid="roadmap-section">
      {!data.profile && (
        <div className="mb-6 bg-slate-950 text-white rounded-2xl px-6 py-5 flex flex-col sm:flex-row sm:items-center gap-4" data-testid="roadmap-eval-banner">
          <div className="w-10 h-10 rounded-lg bg-amber-400/15 flex items-center justify-center shrink-0"><ClipboardList className="w-5 h-5 text-amber-400" /></div>
          <div className="flex-1 min-w-0">
            <p className="font-heading text-xl">Your Career Road Map isn't built yet</p>
            <p className="text-sm text-slate-400 mt-0.5">A 2-minute career evaluation personalizes every step around your goal, location, certifications and target department. You can change any answer later.</p>
          </div>
          <button type="button" onClick={() => setDismissed(false)} className="shrink-0 inline-flex items-center gap-1.5 rounded-full bg-amber-400 text-slate-950 font-semibold text-sm px-5 py-2.5 hover:bg-amber-300 transition-colors" data-testid="roadmap-eval-start">Start career evaluation <ArrowRight className="w-4 h-4" /></button>
        </div>
      )}
      <Dialog open={showSetup} onOpenChange={(o) => { if (!o) closeSetup(); }}>
        <DialogContent className="max-w-2xl p-0 border-0 bg-transparent shadow-none max-h-[92vh] overflow-y-auto [&>button]:text-slate-500 [&>button]:right-6 [&>button]:top-6" data-testid="roadmap-eval-dialog">
          <DialogTitle className="sr-only">Career evaluation</DialogTitle>
          <DialogDescription className="sr-only">Short questions that personalize your Career Road Map.</DialogDescription>
          {showSetup && <RoadmapSetup initial={data.profile || data.seed} onSave={saveProfile} saving={saving} onCancel={null} />}
        </DialogContent>
      </Dialog>
      <div className="flex items-center justify-between gap-3 mb-5 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center"><Map className="w-5 h-5 text-primary" /></div>
          <div>
            <h2 className="font-heading text-2xl font-semibold text-slate-900">Career Road Map</h2>
            <p className="text-sm text-slate-500">{data.profile ? "Personalized to your goal, stage and target department." : "A clear path from zero to hired — personalized around where you already are."}</p>
          </div>
        </div>
      </div>

      {data.profile && data.summary && <SummaryStrip data={data} onChange={(r) => setData(r)} onEditAll={() => setEditing(true)} />}

      {data.profile && (
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
          <div className="px-6 py-5 border-b border-slate-100">
            <div className="flex justify-between text-sm mb-2">
              <span className="font-semibold text-slate-800" data-testid="roadmap-progress-text">{data.completed} of {data.total} milestones completed</span>
              <span className="font-heading font-bold text-amber-600" data-testid="roadmap-progress-pct">{data.progress}%</span>
            </div>
            <div className="h-2.5 rounded-full bg-slate-100 overflow-hidden">
              <motion.div className="h-full bg-amber-500 rounded-full" initial={{ width: 0 }} animate={{ width: `${data.progress}%` }} transition={{ duration: 0.8 }} />
            </div>
            <p className="mt-4 text-xs text-slate-500" data-testid="roadmap-note">Optional and "if applicable" steps are shown but don't count toward your total. Requirements vary by department, city, county and state and can change — verify with the agency you're applying to. Completing every milestone doesn't guarantee eligibility or employment.</p>
          </div>
          <PhaseDetail phase={data.phases.find((p) => p.id === detail)} data={data} open={!!detail} onClose={() => setDetail(null)} isDone={isDone} onToggle={toggle} />
          <div className="divide-y divide-slate-100">
            {data.phases.map((p, idx) => {
              const counted = p.items.filter((i) => i.counts);
              const doneCount = p.items.filter((i) => isDone(i.id)).length;
              const complete = counted.length ? counted.every((i) => isDone(i.id)) : doneCount === p.items.length;
              const isOpen = !!open[p.id];
              const hasKey = p.items.some((i) => i.key);
              const shown = hasKey ? p.items.filter((i) => i.key) : p.items;
              const hidden = hasKey ? p.items.filter((i) => !i.key) : [];
              const hasMore = hidden.length > 0 || DETAIL_PHASES.has(p.id);
              return (
                <div key={p.id} data-testid={`roadmap-phase-${p.id}`}>
                  <div className="flex items-center hover:bg-slate-50">
                    <button onClick={() => setOpen((o) => ({ ...o, [p.id]: !o[p.id] }))} className="flex-1 flex items-center gap-3 min-w-0 pl-6 py-4 text-left" data-testid={`roadmap-phase-toggle-${p.id}`}>
                      <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${complete ? "bg-green-500 text-white" : p.flex ? "bg-slate-100 text-slate-400" : "bg-slate-900 text-white"}`}>{complete ? "✓" : idx + 1}</span>
                      <span className={`font-heading text-lg truncate ${complete ? "text-slate-400 line-through" : "text-slate-900"}`}>{p.title}</span>
                      {p.flex && <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 shrink-0">if applicable</span>}
                      <span className="text-xs text-slate-400 shrink-0">{doneCount}/{p.items.length}</span>
                    </button>
                    <PhaseInfo phase={p} />
                    <button onClick={() => setOpen((o) => ({ ...o, [p.id]: !o[p.id] }))} className="pl-4 pr-6 py-4" aria-label={isOpen ? "Collapse" : "Expand"} data-testid={`roadmap-phase-chevron-${p.id}`}><ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${isOpen ? "rotate-180" : ""}`} /></button>
                  </div>
                  {isOpen && (
                    <div className="px-6 pb-4">
                      {p.blurb && <p className="text-xs text-slate-500 mb-2" data-testid={`roadmap-blurb-${p.id}`}>{p.blurb}</p>}
                      <ul className="space-y-0.5">{shown.map((i) => <Milestone key={i.id} i={i} on={isDone(i.id)} onToggle={toggle} onGo={navigate} />)}</ul>
                      <div className="mt-3 flex flex-wrap items-center gap-4">
                        {hasMore && <button type="button" onClick={() => setDetail(p.id)} className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3.5 py-1.5 text-xs font-semibold text-slate-700 hover:border-primary hover:text-primary transition-colors" data-testid={`roadmap-learn-more-${p.id}`}><BookOpen className="w-3.5 h-3.5" /> Learn more{hidden.length ? ` · ${hidden.length} more step${hidden.length === 1 ? "" : "s"}` : ""}</button>}
                        {p.link && <button onClick={() => navigate(p.link)} className="inline-flex items-center gap-1 text-sm font-semibold text-primary hover:underline" data-testid={`roadmap-link-${p.id}`}>{p.link_label} <ArrowRight className="w-4 h-4" /></button>}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </section>
  );
}
